import { resourceDisplayName } from "./resourceTimings";

/**
 * Attribution for a Long Animation Frame — WHY a frame was long. Pure logic over
 * the LoAF entry shape (structural so it's testable without the DOM types).
 *
 * The browser gives per-script timing (which function ran, from which file, how
 * much forced style/layout it triggered) AND the frame's internal timeline
 * (`renderStart`, `styleAndLayoutStart`). We use both: when JS dominated we name
 * the script; when the RENDERING cycle dominated (style & layout, rAF/observers,
 * or non-script GC/compile) we say that instead — and, crucially, we DON'T offer
 * a "source" to open, because there is no script to blame.
 */
export interface ScriptTimingLike {
    sourceURL?: string;
    sourceFunctionName?: string;
    invoker?: string;
    invokerType?: string;
    duration?: number;
    /** forced (synchronous) style/layout the script triggered — "layout thrash" */
    forcedStyleAndLayoutDuration?: number;
    /** character OFFSET into the source (not a line — LoAF gives no line:col) */
    sourceCharPosition?: number;
}
export interface LongFrameLike {
    /** frame start (PerformanceEntry.startTime), ms */
    startTime?: number;
    duration?: number;
    blockingDuration?: number;
    /** start of the rendering cycle (rAF callbacks, style/layout, Resize/IntersectionObserver) */
    renderStart?: number;
    /** start of style & layout within the rendering cycle */
    styleAndLayoutStart?: number;
    scripts?: ScriptTimingLike[];
}

/** What a long frame's time is attributed to, and (if JS) where its source is. */
export interface LongFrameAnalysis {
    /** human "why" — e.g. "render in three.js@4200" or "style & layout — 120 ms" */
    readonly detail: string;
    /** dominant script's URL — ONLY when the frame is script-bound (else undefined
     *  so callers don't offer a misleading "open source" for rendering-bound work) */
    readonly url?: string;
    readonly charPosition?: number;
    /** the frame was dominated by the RENDERING cycle (style/layout or rAF), not JS */
    readonly renderBound: boolean;
}

/** The heaviest script that actually ran (duration > 0), or undefined. */
function dominantScript(entry: LongFrameLike): ScriptTimingLike | undefined {
    let top: ScriptTimingLike | undefined;
    for (const s of entry.scripts ?? []) if (!top || (s.duration ?? 0) > (top.duration ?? 0)) top = s;
    return top && (top.duration ?? 0) > 0 ? top : undefined;
}

function scriptLabel(s: ScriptTimingLike): string {
    const fn = s.sourceFunctionName?.trim() || s.invoker?.trim() || s.invokerType || "script";
    const file = s.sourceURL ? resourceDisplayName(s.sourceURL) : "";
    // LoAF gives a char OFFSET, not line:col — append it raw (cheap; no fetch)
    const pos = typeof s.sourceCharPosition === "number" && s.sourceCharPosition >= 0 ? `@${s.sourceCharPosition}` : "";
    return file ? `${fn} in ${file}${pos}` : fn;
}

/**
 * Classify a long frame into script-bound vs rendering-bound, using the phase
 * timeline when the browser provides it:
 *
 *   startTime ─[tasks incl. scripts]→ renderStart ─[rAF, observers]→ styleAndLayoutStart ─[style & layout]→ end
 *
 * The single biggest of { dominant script, style&layout, rAF/observers, non-script
 * pre-render } wins. Only a script win carries a source URL to open.
 */
export function analyzeLongFrame(entry: LongFrameLike): LongFrameAnalysis {
    const scripts = entry.scripts ?? [];
    const top = dominantScript(entry);
    const forced = scripts.reduce((sum, s) => sum + (s.forcedStyleAndLayoutDuration ?? 0), 0);
    const forcedNote = forced >= 5 ? ` · ${Math.round(forced)} ms layout` : "";

    const start = entry.startTime, dur = entry.duration;
    if (typeof start === "number" && typeof dur === "number" && dur > 0) {
        const end = start + dur;
        const rs = entry.renderStart, sls = entry.styleAndLayoutStart;
        // renderStart is 0 (or absent) when NO rendering happened this frame — then the
        // rendering cycle is empty and the whole frame is pre-render tasks.
        const hasRender = typeof rs === "number" && rs >= start && rs < end;
        const cycleStart = hasRender ? (rs as number) : end;
        const hasSls = hasRender && typeof sls === "number" && sls >= (rs as number) && sls <= end;
        const styleLayout = hasSls ? end - (sls as number) : 0;
        const rafObs = hasRender ? (hasSls ? (sls as number) - (rs as number) : end - (rs as number)) : 0;
        const scriptTotal = scripts.reduce((s, x) => s + (x.duration ?? 0), 0);
        const nonScriptPre = Math.max(0, (cycleStart - start) - scriptTotal); // GC/compile/other

        const contenders = [
            { k: "script", ms: top?.duration ?? 0 },
            { k: "style-layout", ms: styleLayout },
            { k: "raf", ms: rafObs },
            { k: "other", ms: nonScriptPre },
        ].sort((a, b) => b.ms - a.ms);
        const dom = contenders[0];

        if (dom.k === "script" && top) {
            return {
                detail: scriptLabel(top) + forcedNote,
                url: top.sourceURL || undefined,
                charPosition: typeof top.sourceCharPosition === "number" && top.sourceCharPosition >= 0 ? top.sourceCharPosition : undefined,
                renderBound: false,
            };
        }
        if (dom.ms > 0) {
            const label = dom.k === "style-layout" ? "style & layout"
                : dom.k === "raf" ? "rendering (rAF / observers)"
                : "GC / compile / other";
            return { detail: `${label} — ${Math.round(dom.ms)} ms`, renderBound: dom.k === "style-layout" || dom.k === "raf" };
        }
    }

    // No usable phase timeline: fall back to the dominant script, else a generic note.
    if (top) {
        return {
            detail: scriptLabel(top) + forcedNote,
            url: top.sourceURL || undefined,
            charPosition: typeof top.sourceCharPosition === "number" && top.sourceCharPosition >= 0 ? top.sourceCharPosition : undefined,
            renderBound: false,
        };
    }
    return { detail: "rendering / GC / compile" + forcedNote, renderBound: true };
}

/** Short human "why" for a long frame (see {@link analyzeLongFrame}). */
export function describeLongFrame(entry: LongFrameLike): string {
    return analyzeLongFrame(entry).detail;
}

export type LongFrameSeverity = "minor" | "major";

/**
 * How prominently (if at all) to plot a long frame on the events strip:
 *  - `"major"` — a real stall (`duration >= majorMs`); plot red. Duration-based so a
 *    rendering-bound stall (long but no blocking TASK, `blockingDuration` 0) still counts.
 *  - `"minor"` — crossed the 50ms LoAF floor but under `majorMs` AND actually blocked a
 *    little (`blockingDuration > 0`); plot grey.
 *  - `null` — long but non-blocking (`blockingDuration` 0, under `majorMs`); skip it, so a
 *    smooth page (whose 50–80ms frames are mostly render/GC with no blocking) stays quiet
 *    instead of flooding the strip with "long but fine" ticks.
 */
export function classifyLongFrame(entry: LongFrameLike, majorMs: number): LongFrameSeverity | null {
    if ((entry.duration ?? 0) >= majorMs) return "major";
    return (entry.blockingDuration ?? 0) > 0 ? "minor" : null;
}

/** The dominant script's source location — only when the frame is SCRIPT-bound
 *  (rendering-bound frames have no script to open). Enough to fetch the script and
 *  pull a section around the offset (minified files are one huge line). */
export function longFrameSourceLocation(entry: LongFrameLike): { url?: string; charPosition?: number } {
    const a = analyzeLongFrame(entry);
    return { url: a.url, charPosition: a.charPosition };
}

/** The dominant script's FULL sourceURL (unshortened) — for the copy path. */
export function longFrameSourceURL(entry: LongFrameLike): string | undefined {
    return longFrameSourceLocation(entry).url;
}
